import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { Content } from './content';
import { retry } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AlertService } from '@app/_services';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class ContentService {

	statuses: any = [
		{ value: 'DRAFT', name: 'Draft' },
		{ value: 'REVIEW', name: 'Review' },
		{ value: 'APPROVED', name: 'Approved' },
		{ value: 'PUBLISHED', name: 'Published' },
		{ value: 'ARCHIVED', name: 'Archived' }
	];

	contentTypes: any[] = [];

	constructor(
		private http: HttpClient,
		private alertService: AlertService,
		private sharedService: SharedService,
	) {
		this.loadContentTypes();
	}

	urlEndpoint = 'contents';

	loadContentTypes(): void {
		this.http.get<ApiResponse>(`${environment.apiUrl}/public/content-types`).subscribe({
			next: (res: any) => {
				this.contentTypes = res?.data?.contentTypes || [];
			},
			error: () => { this.contentTypes = []; }
		});
	}

	findAllContentTypes(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/public/content-types`).pipe(retry({ count: 2, delay: 1500 }));
	}

	add(content: Content): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, content);
	}

	update(content: Content): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, content);
	}

	delete(contentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${contentId}`);
	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta: any, globalFilter: any, filters: any): Observable<ApiResponse> {
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}

	generateContent(contentTypeId: number, locationId: number, developmentId: number, shortTermRentalId: number): Observable<ApiResponse> {
		const body: any = { contentTypeId: contentTypeId };
		if (locationId) body.locationId = locationId;
		if (developmentId) body.developmentId = developmentId;
		if (shortTermRentalId) body.shortTermRentalId = shortTermRentalId;
		return this.http.post<ApiResponse>(`${environment.apiUrl}/contents/generate`, body);
	}

	getUploadPresignedUrl(fileName: string, bucketName?: string): Observable<ApiResponse> {
		const body = { fileName: fileName, bucketName: bucketName || 'img.kbmhawaii.com' };
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/media/upload-presign`, body);
	}

	getRevisions(contentId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/public/contents/${contentId}/revisions`).pipe(retry(1));
	}

	revertToRevision(contentId: number, revisionId: number): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/admin/contents/${contentId}/revisions/${revisionId}/revert`, {});
	}

	transitionWorkflow(contentId: number, toStatus: string, comment: string, assignedToUserId: number): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/admin/contents/${contentId}/workflow/transition`, { toStatus, comment, assignedToUserId });
	}

	getWorkflowQueue(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/admin/contents/workflow/queue`).pipe(retry(1));
	}

	generatePillarPage(topicKeyword: string, contentIds: number[]): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/pillar/generate`, { topicKeyword, contentIds });
	}

	getAutolinkSuggestions(contentId: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${contentId}/autolink-suggestions`).pipe(retry(1));
	}

	applyAutolinkSuggestion(suggestionId: number): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/autolink-suggestions/${suggestionId}/apply`, {});
	}

	dismissAutolinkSuggestion(suggestionId: number): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/autolink-suggestions/${suggestionId}/dismiss`, {});
	}

	getByContentType(contentType: string, page = 0, size = 200): Observable<ApiResponse> {
		const params: any = { page, size, 'filters[contentType][value]': contentType };
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params }).pipe(retry(1));
	}

	regenMissingMeta(): Observable<any> {
		return this.http.post(`${environment.apiUrl}/content/admin/regen-meta`, {});
	}
}
